package com.karyadina.service

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.SupervisorJob
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

enum class GlobalTimerState {
    IDLE,
    RUNNING,
    PAUSED,
    COMPLETED
}

data class TimerStateSnapshot(
    val state: GlobalTimerState = GlobalTimerState.IDLE,
    val sessionId: String? = null,
    val title: String? = null,
    val elapsedTimeSeconds: Long = 0,
    val totalDurationSeconds: Long = 0
)

class SessionTimerManager(
    private val settings: com.russhwolf.settings.Settings,
    private val studyPlannerService: StudyPlannerServiceImpl
) {
    private val scope = CoroutineScope(Dispatchers.Main + SupervisorJob())
    
    private val _timerState = MutableStateFlow(TimerStateSnapshot())
    val timerState = _timerState.asStateFlow()

    private var timerJob: Job? = null
    private var startTime: Long = 0
    private var accumulatedTime: Long = 0
    
    companion object {
        private const val KEY_SESSION_ID = "timer_session_id"
        private const val KEY_START_TIME = "timer_start_time"
        private const val KEY_ACCUMULATED = "timer_accumulated"
        private const val KEY_TITLE = "timer_title"
        private const val KEY_DURATION = "timer_duration"
        private const val KEY_STATE = "timer_state"
    }
    
    init {
        restoreState()
    }

    @OptIn(ExperimentalTime::class)
    fun start(id: String, title: String, durationSeconds: Long = 0) {
        stop() // Ensure clean start

        // Fetch existing progress
        val session = studyPlannerService.getSession(id)
        val initialElapsedSeconds = (session?.actualDurationMinutes ?: 0) * 60L

        _timerState.update {
            it.copy(
                state = GlobalTimerState.RUNNING,
                sessionId = id,
                title = title,
                totalDurationSeconds = durationSeconds,
                elapsedTimeSeconds = initialElapsedSeconds
            )
        }
        
        accumulatedTime = initialElapsedSeconds * 1000
        startTime = Clock.System.now().toEpochMilliseconds()
        startTicker()
        saveState()
    }

    fun pause() {
        if (_timerState.value.state == GlobalTimerState.RUNNING) {
            timerJob?.cancel()
            // capturing current elapsed time
            accumulatedTime = _timerState.value.elapsedTimeSeconds * 1000
            _timerState.update { it.copy(state = GlobalTimerState.PAUSED) }
            saveState()
        }
    }

    @OptIn(ExperimentalTime::class)
    fun resume() {
        if (_timerState.value.state == GlobalTimerState.PAUSED) {
            startTime = Clock.System.now().toEpochMilliseconds()
            _timerState.update { it.copy(state = GlobalTimerState.RUNNING) }
            startTicker()
            saveState()
        }
    }

    fun stop() {
        val currentState = _timerState.value
        
        // SYNC DATA BEFORE STOPPING
        // Only sync if NOT already completed (because complete() acts as a sync point)
        if (currentState.sessionId != null && 
            currentState.elapsedTimeSeconds > 0 && 
            currentState.state != GlobalTimerState.COMPLETED) {
            syncSessionToDb(currentState.sessionId, currentState.elapsedTimeSeconds)
        }

        timerJob?.cancel()
        _timerState.update { 
            it.copy(
                state = GlobalTimerState.IDLE, 
                sessionId = null,
                title = null,
                elapsedTimeSeconds = 0,
                totalDurationSeconds = 0 
            ) 
        }
        clearSavedState()
    }
    
    private fun syncSessionToDb(sessionId: String, elapsedSeconds: Long) {
        // Find session in DB
        val session = studyPlannerService.getSession(sessionId)
        if (session != null) {
            // Use rounding to capture partial minutes (e.g., 40s -> 1m)
            val totalElapsedMinutes = kotlin.math.round(elapsedSeconds / 60.0).toInt()
            
            // Only update if we have meaningful progress or it's different
            // Note: Since we initialize with stored value, this overwrites correctly.
            val newActual = totalElapsedMinutes
            
            // Check if it's completed (either met target or just updating progress)
            val isCompleted = session.targetDurationMinutes > 0 && newActual >= session.targetDurationMinutes
            
            val updatedSession = session.copy(
                actualDurationMinutes = newActual,
                completed = isCompleted || session.completed
            )
            studyPlannerService.updateSession(updatedSession)
        }
    }

    @OptIn(ExperimentalTime::class)
    private fun startTicker() {
        timerJob?.cancel()
        timerJob = scope.launch {
            while (true) {
                delay(1000)
                val now = Clock.System.now().toEpochMilliseconds()
                val elapsedSinceStart = now - startTime
                val totalElapsed = (accumulatedTime + elapsedSinceStart) / 1000

                _timerState.update {
                    it.copy(elapsedTimeSeconds = totalElapsed)
                }
                
                // Check for completion if duration is set
                 val duration = _timerState.value.totalDurationSeconds
                 if (duration > 0 && totalElapsed >= duration) {
                     complete() // Auto-complete
                     break
                 }
            }
        }
    }
    
    private fun complete() {
        val currentState = _timerState.value
        
        // Sync as complete
         if (currentState.sessionId != null) {
            // First sync the final elapsed time to the DB
            // This ensures 'actualDurationMinutes' is updated (and likely marks it complete via logic there too)
            syncSessionToDb(currentState.sessionId, currentState.elapsedTimeSeconds)
            
            // Explicitly ensure 'completed' flag is set (redundant but safe)
            studyPlannerService.completeSession(currentState.sessionId)
        }
        
        timerJob?.cancel()
        _timerState.update { it.copy(state = GlobalTimerState.COMPLETED) }
        saveState()
    }

    private fun saveState() {
        val s = _timerState.value
        settings.putString(KEY_SESSION_ID, s.sessionId ?: "")
        settings.putLong(KEY_START_TIME, startTime)
        settings.putLong(KEY_ACCUMULATED, accumulatedTime)
        settings.putString(KEY_TITLE, s.title ?: "")
        settings.putLong(KEY_DURATION, s.totalDurationSeconds)
        settings.putString(KEY_STATE, s.state.name)
    }

    private fun clearSavedState() {
        settings.remove(KEY_SESSION_ID)
        settings.remove(KEY_START_TIME)
        settings.remove(KEY_ACCUMULATED)
        settings.remove(KEY_TITLE)
        settings.remove(KEY_DURATION)
        settings.remove(KEY_STATE)
    }
    
    @OptIn(ExperimentalTime::class)
    fun restoreState() {
        val savedId = settings.getString(KEY_SESSION_ID, "")
        if (savedId.isNotBlank()) {
            val savedStateName = settings.getString(KEY_STATE, GlobalTimerState.IDLE.name)
            val savedState = GlobalTimerState.valueOf(savedStateName)
            
            if (savedState == GlobalTimerState.IDLE) return 

            val savedTitle = settings.getString(KEY_TITLE, "")
            val savedDuration = settings.getLong(KEY_DURATION, 0L)
            
            startTime = settings.getLong(KEY_START_TIME, 0L)
            accumulatedTime = settings.getLong(KEY_ACCUMULATED, 0L)
            
            // Calculate current elapsed if active
            var currentElapsed = 0L
             if (savedState == GlobalTimerState.RUNNING) {
                val now = Clock.System.now().toEpochMilliseconds()
                val elapsedSinceStart = now - startTime
                currentElapsed = (accumulatedTime + elapsedSinceStart) / 1000
                
                // If we exceeded duration while away, auto-complete
                if (savedDuration > 0 && currentElapsed >= savedDuration) {
                    _timerState.update { 
                        it.copy(
                            state = GlobalTimerState.COMPLETED,
                            sessionId = savedId,
                            title = savedTitle,
                            totalDurationSeconds = savedDuration,
                            elapsedTimeSeconds = currentElapsed
                        )
                    }
                    return
                } else {
                     startTicker() // Resume ticking
                }
             } else {
                 currentElapsed = accumulatedTime / 1000
             }

            _timerState.update { 
                it.copy(
                    state = savedState,
                    sessionId = savedId,
                    title = savedTitle,
                    totalDurationSeconds = savedDuration,
                    elapsedTimeSeconds = currentElapsed
                )
            }
        }
    }
}
